import { useCallback, useEffect, useMemo, useState } from 'react';
import { UseCartType } from '../../types';
import { ToLocalStringConvert } from '../../Utils/toLocalStringConvert';
import { DiamondLabels } from '../../Utils/commonData';
import { getNoBands } from '../../Utils/getNoBands';
import { getRingPrice } from '../../Utils/getRingPriceValue';
import CONSTANTS from "../../constant/general";
import { imageColor, ringSettingParams, studExtraImageView } from "../../constant/generalData";
import { useMediaQuery } from "react-responsive";
import { getBuildSearchParams } from "../../Utils/general";
import { useFinalizeYourDesign } from "../FinalizeYourDesign/useFinalizeYourDesign";

const useCart = (props: UseCartType) => {
  const {
    ring,
    footer,
    filter,
    diamond,
    setFooterToggle,
    setRedirectEditPage,
    setSettingTabIconTrue,
    setDiamondTabIconTrue,
    setCartImageSuccess,
    setCartThumbImageSuccess,
    setBackToSelectDiamondPage,
    toggleScrollTop,
    setRingOptions,
    setRingExtraData,
    setRemoveRingOption,
    setBandTabIndex,
    setFilter,
    cart,
    setAfterRingOptions,
    instanceData: { screenManager },
    setPriceLoading,
    setRingPrice,
    setRingPriceError
  } = props;

  const isMobile = useMediaQuery({ query: '(max-width: 767px)' });
  const isTablet = useMediaQuery({ query: '(min-width: 768px) and (max-width: 1023px)' });
  const imageResolution = isMobile ? '800' : isTablet ? '1200' : '2000';
  const [isShowDiamondType, setShowDiamondType] = useState(false);
  const [isShowRingType, setShowRingType] = useState(false);
  const [isShowDoubleFirstBandType, setIsShowDoubleFirstBandType] = useState(false);
  const [isShowDoubleSecondBandType, setIsShowDoubleSecondBandType] = useState(false);
  const [image, setImage] = useState<string[]>([]);
  const [thumbImage, setThumbImage] = useState<string[]>([]);
  const urlData = useMemo(() => {
    return (screenManager as any).parseURL(window.location.href);
  }, [screenManager, window.location.href]);
  const isStuds = useMemo(() => urlData.command === 'stud-earrings',[urlData.command]);
  const {
    certificate_link,
    scs_certificate_link,
    certification,
    source,
    carat,
    shape,
    cut,
    color,
    clarity,
    measurement,
    table_per,
    depth,
    symmetry,
    polish,
    girdle,
    culet,
    fluorecence,
    Center_Diamond_Shape,
    b2c_price: diamond_b2c_price
  } = diamond.details;

  const {
    Ring_Style: RingStyleName,
    Color: ColorName,
    Metal: MetalName,
  } = ring.selectedRingDetails;

  const {
    Ring_Style,
    Diamond_Type,
    Diamond_Shape,
    Center_Diamond_Size,
    Ring_Crown,
    Ring_Side,
    Color,
    Crown_Metal,
    Metal,
    Wedding_Band,
    Side_Stones,
    Single_First_Metal_Band,
    Double_First_Metal_Band,
    Double_Second_Metal_Band,
    Earring_Back
  } = ring.options;

  const {
    handleFinalizeYourDesign,
    loading,
    showRingDetails,
    isSideStone,
    FirstBandMetalColor,
    SecondBandMetalColor,
    FirstBand,
    headColor,
    SecondBand,
  } = useFinalizeYourDesign();

  const { ring_price, left_band_price, right_band_price } = ring.ringPrice;

  const { Label, image_cache_param } = ring.extraData;

  useEffect(() => {
    const cartView = document.querySelector('#cartView') as HTMLElement;
    if (cartView) {
      cartView.addEventListener('keydown', function(event) {
        const isTabPressed = event.key === 'Tab' || event.keyCode === 9;
        if (isTabPressed) {
          const focusableElements = cartView.querySelectorAll<HTMLElement>('a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])');
          const firstFocusable = focusableElements[0];
          const lastFocusable = focusableElements[focusableElements.length - 1];
          if (event.shiftKey && document.activeElement === firstFocusable) {
            event.preventDefault();
            firstFocusable.focus();
          } else if (!event.shiftKey && document.activeElement === lastFocusable) {
            event.preventDefault();
            lastFocusable.focus();
          }
        }
      });
    }
  }, []);

  useEffect(() => {
    const getNoBandsData = async () => {
      if (Ring_Style && Ring_Crown && Diamond_Shape && Ring_Side && !!ring.extraData.Label) {
        const extraData = await getNoBands(ring.extraData, setRingExtraData, ring.options.Ring_Style, ring.options.Ring_Crown, ring.options.Ring_Side, ring.options.Diamond_Shape);
        if (extraData?.Bands === 0) {
          setBandTabIndex(0);
          setRingOptions('Wedding_Band', 'No Band');
          ['Single_First_Band', 'Single_First_Metal_Band', 'Double_First_Band', 'Double_First_Metal_Band', 'Double_Second_Band', 'Double_Second_Metal_Band'].map((value) => setRemoveRingOption(value));
        } else if (extraData?.Bands === 1) {
          if (Wedding_Band === 'Double') {
            setBandTabIndex(1);
            setRingOptions('Wedding_Band', 'Single');
            ['Double_First_Band', 'Double_First_Metal_Band', 'Double_Second_Band', 'Double_Second_Metal_Band'].map((value) => setRemoveRingOption(value));
          }
        }
      }
    };
    getNoBandsData();
  }, [Ring_Style, Ring_Crown, Diamond_Shape, Ring_Side, Center_Diamond_Shape, ring.extraData.Label]);

  useEffect(() => {
    const controller = new AbortController();

    if (footer.toggle && Object.keys(ring.options).length > 0 && footer.isRedirectToEdit) {
      let ringPriceData = "";
      if(isStuds){
        ringPriceData = `${footer.styleID}-${Ring_Style}${Ring_Crown}-${Diamond_Shape}-${Center_Diamond_Size}-${Ring_Side}-${(Crown_Metal ?? Metal)}-${Metal}-WG-${Diamond_Type}-${Side_Stones}-${Earring_Back}`;
      }else {
        ringPriceData = `${footer.styleID}-${Ring_Style}${Ring_Crown}-RND-${Center_Diamond_Size}-${Ring_Side}-${(Crown_Metal ?? Metal)}-${Metal}-WG-${Diamond_Type}-${Side_Stones}`;
      }
      const priceData = {
        sku: `'${ringPriceData}'`, order_items: Wedding_Band === 'Single' ? 2 : Wedding_Band === 'Double' ? 3 : 1
      };
      getRingPrice(setPriceLoading, setRingPrice, setRingPriceError, priceData, controller.signal);
    }
    return () => {
      controller.abort();
    }
  }, [ footer.toggle, Side_Stones, Earring_Back, Diamond_Shape, Center_Diamond_Size, Diamond_Type ]);

  useEffect(() => {
    const fetchData = async () => {
      if (footer.styleID && Ring_Crown && Diamond_Shape && Center_Diamond_Size && Ring_Side) {
        setImage([]);
        setThumbImage([]);

        const style = footer.styleID.includes('RP') ? `${footer.styleID}-${Ring_Crown}-${Diamond_Shape}-${Center_Diamond_Size}-${Ring_Side}` : `${footer.styleID}-${Ring_Style}${Ring_Crown}-${Diamond_Shape}-${Center_Diamond_Size}-${Ring_Side}`;
        const uniqueName = footer.styleID.includes('RP') ? `${footer.styleID}${Ring_Crown}${Diamond_Shape}${Center_Diamond_Size}${Ring_Side}` : `${footer.styleID}${Ring_Style}${Ring_Crown}${Diamond_Shape}${Center_Diamond_Size}${Ring_Side}${headColor}${Color}${(Single_First_Metal_Band || Double_First_Metal_Band) ? `${(Single_First_Metal_Band || Double_First_Metal_Band)?.split('_')[1]}` : ''}${Double_Second_Metal_Band ? `${Double_Second_Metal_Band?.split('_')[1]}` : ''}${Wedding_Band === 'Double' ? 'C1' : Wedding_Band === 'Single' ? 'C2' : 'C3'}`;

        const views = isStuds ? ["V1", "V2", "V3", "V4", "V5", studExtraImageView[Earring_Back as string], "V9"] : ["V1", "V2", "V3", "V4"];
        views.forEach((view: string) => {
          const imagePath = `${process.env.REACT_APP_IMAGE_KEY}/${style}/${headColor}-${Color}${(Single_First_Metal_Band || Double_First_Metal_Band) ? `-${(Single_First_Metal_Band || Double_First_Metal_Band)?.split('_')[1]}` : ''}${Double_Second_Metal_Band ? `-${Double_Second_Metal_Band?.split('_')[1]}` : ''}/${Wedding_Band === 'Double' ? 'C1' : Wedding_Band === 'Single' ? 'C2' : 'C3'}/${view}/${imageResolution}/${imageColor(view)}/hq/DIA-${Side_Stones}/${uniqueName}${view}${imageResolution}.jpg?${image_cache_param}`;
          const thumbPath = `${process.env.REACT_APP_IMAGE_KEY}/${style}/${headColor}-${Color}${(Single_First_Metal_Band || Double_First_Metal_Band) ? `-${(Single_First_Metal_Band || Double_First_Metal_Band)?.split('_')[1]}` : ''}${Double_Second_Metal_Band ? `-${Double_Second_Metal_Band?.split('_')[1]}` : ''}/${Wedding_Band === 'Double' ? 'C1' : Wedding_Band === 'Single' ? 'C2' : 'C3'}/${view}/150/${imageColor(view)}/hq/DIA-${Side_Stones}/${uniqueName}${view}150.jpg?${image_cache_param}`;
          setImage((prev: string[]) => {
            return [...prev, imagePath];
          });
          setThumbImage((prev: string[]) => {
            return [...prev, thumbPath];
          });
        });
      }
    };
    fetchData();
  }, [image_cache_param, Earring_Back, Side_Stones, headColor, Ring_Style, Ring_Crown, Diamond_Shape, Center_Diamond_Size, Ring_Side, Metal, Color, Diamond_Type, Wedding_Band, Single_First_Metal_Band, Double_Second_Metal_Band, Double_First_Metal_Band]);

  useEffect(() => {
    setCartImageSuccess([...image]);
  }, [image]);

  useEffect(() => {
    setCartThumbImageSuccess([...thumbImage]);
  }, [thumbImage]);

  const showDiamondDetails = useMemo(() => {
    return {
      'Certificate Link': certificate_link,
      'SCS Certificate Link': scs_certificate_link,
      certification: certification,
      'Diamond Type': source,
      'Carat Weight': carat,
      Shape: shape,
      Cut: cut,
      Color: color,
      Clarity: clarity,
      Measurements: measurement,
      Table: `${table_per ? `${table_per}%` : ''}`,
      Depth: `${depth ? `${depth}%` : ''}`,
      Symmetry: symmetry,
      Polish: polish,
      Girdle: girdle,
      Culet: culet,
      Fluorecence: fluorecence
    };
  }, [diamond.details]);

  const handleDiamondAction = useCallback(() => {
    setShowDiamondType((type) => !type);
  }, [setShowDiamondType]);

  const handleRingAction = useCallback(() => {
    setShowRingType((type) => !type);
  }, [setShowRingType]);

  // Function of Finalize or Update Product Data
  const handleReviewClick = async () => {
    await handleFinalizeYourDesign(isStuds);
  };

  const editDiamondAction = useCallback(() => {
    toggleScrollTop(true);
    setFooterToggle();
    setSettingTabIconTrue();
    setRedirectEditPage(true);
    setBackToSelectDiamondPage(true);
    document.body.classList.add('sticky_filter');
    const { Diamond_Shape, Diamond_Type } = ring.options;
    if (ring.extraData.Label && ring.extraData.Label !== '' && !Object.keys(filter.options).includes('source')) {
      if (ring.extraData.Label !== 'Generic') {
        setFilter('source', 'LGN');
        const Options: { [key: string]: string | boolean | string[] } = {
          source: Diamond_Type, shape: Diamond_Shape,
          // lab: ['GCAL']
        };
        ring.extraData.Label === 'Reve' && (Options['scs'] = true);
        setAfterRingOptions(Options as { [key: string]: string | string[] });
      }
    }
    (screenManager as any).changeScreen({
      viewName:     'diamond',
      id:           null,
      searchParams: getBuildSearchParams(window.location)
    });
  }, [document.body, toggleScrollTop, setFooterToggle, setSettingTabIconTrue, setRedirectEditPage, screenManager, setBackToSelectDiamondPage, ring.extraData, filter.options, setFilter, setAfterRingOptions, ring.options]);

  const editRingAction = useCallback(() => {
    setFooterToggle();
    const screen = (footer.styleID.includes('RP') || ring.journey === CONSTANTS.SETTING)? 'setting' : 'dyo';
    setDiamondTabIconTrue(screen);
    setRedirectEditPage(true);
    const params = {
      ...(footer.styleID.includes('RB') && {
        [ringSettingParams.Ring_Crown] : ring.options.Ring_Crown,
        [ringSettingParams.Ring_Style] : ring.options.Ring_Style,
        [ringSettingParams.Ring_Side] : ring.options.Ring_Side
      })
    }
    if (Object.keys(diamond.details).length > 0) {
      (screenManager as any).changeScreen({
        viewName: screen,
        styleId: footer.styleID,
        id: diamond.details.id,
        params,
        searchParams: getBuildSearchParams(window.location)
      });
    } else {
      (screenManager as any).changeScreen({
        viewName: screen,
        styleId: footer.styleID,
        params,
        id:           null,
        searchParams: getBuildSearchParams(window.location)
      });
    }
  }, [ ring.options, setFooterToggle, setDiamondTabIconTrue, setRedirectEditPage, screenManager, diamond.details, footer.styleID ]);

  const handleDoubleFirstBandAction = useCallback(() => {
    setIsShowDoubleFirstBandType((type) => !type);
  }, [setIsShowDoubleFirstBandType]);

  const handleDoubleSecondBandAction = useCallback(() => {
    setIsShowDoubleSecondBandType((type) => !type);
  }, [setIsShowDoubleSecondBandType]);

  const allDetails = [{
    showDetails: Object.keys(diamond.details).length > 0 ? showDiamondDetails : [],
    title: `${carat} ct. ${DiamondLabels[shape as string]} ${source} Diamond`.trim(),
    price: `$${ToLocalStringConvert(diamond_b2c_price ? +diamond_b2c_price : 0)}`, ...(!cart.isOrderExist && { editAction: editDiamondAction }),
    moreAction: handleDiamondAction,
    isShowMore: isShowDiamondType
  }, {
    showDetails: Object.keys(ring.selectedRingDetails).length > 0 ? showRingDetails : [],
    title: `${(Label?.toLowerCase() !== 'generic' && Label) ? ((Label === 'Reve') ? 'rêve ' : `${Label} `) : ''}${RingStyleName} ${source} Diamond ${isSideStone} Setting in ${MetalName} ${ColorName ? ColorName : ''} ${MetalName?.toLowerCase() !== 'platinum' ? 'Gold' : ''}`.trim(),
    price: `$${ToLocalStringConvert(+ring_price - +left_band_price - +right_band_price)}`, ...(!cart.isOrderExist && { editAction: editRingAction }),
    moreAction: handleRingAction,
    isShowMore: isShowRingType
  }, {
    showDetails: Double_First_Metal_Band || Single_First_Metal_Band ? FirstBand : [],
    title: `${(Label?.toLowerCase() !== 'generic' && Label) ? ((Label === 'Reve') ? 'rêve ' : `${Label} `) : ''}${source} Diamond Wedding Band in
      ${FirstBandMetalColor} ${FirstBandMetalColor?.split(' ')[0]?.toLowerCase() !== 'platinum' ? 'Gold' : ''}`.trim(),
    price: `$${ToLocalStringConvert(+left_band_price)}`, ...(!cart.isOrderExist && { editAction: editRingAction }),
    moreAction: handleDoubleFirstBandAction,
    isShowMore: isShowDoubleFirstBandType
  }, {
    showDetails: Double_Second_Metal_Band ? SecondBand : [],
    title: `${(Label?.toLowerCase() !== 'generic' && Label) ? ((Label === 'Reve') ? 'rêve ' : `${Label} `) : ''}${source} Diamond Wedding Band in
      ${SecondBandMetalColor} ${SecondBandMetalColor?.split(' ')[0]?.toLowerCase() !== 'platinum' ? 'Gold' : ''}`.trim(),
    price: `$${ToLocalStringConvert(+right_band_price)}`, ...(!cart.isOrderExist && { editAction: editRingAction }),
    moreAction: handleDoubleSecondBandAction,
    isShowMore: isShowDoubleSecondBandType
  }];

  useEffect(() => {
    if (footer.toggle && footer.isShowFooter) {
      const cartView = document.querySelector('#cartView') as HTMLElement;
      if (cartView) {
        const firstTabbableElement = cartView.querySelector<HTMLElement>('.swiper-wrapper [tabindex]:not([tabindex="-1"])');
        if (firstTabbableElement) {
          setTimeout(() => firstTabbableElement.focus(), 200);
        }
      }
    }
  }, [cart.isCartLoading, footer.toggle, footer.isShowFooter, document.querySelector('#cartView')]);

  return {
    allDetails, diamond_b2c_price, handleReviewClick, ring_price, isLoading:loading
  };
};

export default useCart;
